// Store da PILHA de overlays da ficha (docs/plan/tv-ficha-overlay.md, contrato P0).
//
// A ficha (filme/série) e o player que abre de dentro dela deixam de ser ROTAS e
// viram OVERLAYS empilhados por cima da tela de origem, que NÃO desmonta. O path
// fica PARADO na origem (`/home`, `/movies`, ...) durante todo o fluxo
// card→ficha→player; é isso que devolve scroll+DOM+foco "de graça" no Voltar,
// sem o remount lento (>2,4s medido em tier baixo).
//
// Este módulo é a FONTE DA VERDADE da pilha: só o tipo da camada + as transições.
// É PURO de propósito — nada de foco, nada de Back, nada de React além do hook de
// leitura. Quem orquestra foco/Back é o DetailOverlayHost (o árbitro). As frentes
// VOD (P1) e Série (P2) importam DAQUI, nunca da prosa do plano.
//
// Padrão de store: module-level + Set de listeners + useSyncExternalStore, igual
// ao trial-block-store. O snapshot devolve SEMPRE a mesma referência de array
// enquanto nada muda (troca imutável a cada mutação) — senão o useSyncExternalStore
// entra em loop.

import { useSyncExternalStore } from 'react'

/** Uma camada da pilha. O `kind` discrimina a união. */
export type OverlayLayer =
  | {
      kind: 'movie-detail'
      id: string
      /**
       * A focusKey do card/botão que estava focado na ORIGEM quando a ficha
       * abriu. É pra ONDE o foco volta quando esta camada é fechada (pop). Pode
       * não existir mais no fecho (card virtualizado pra fora) — por isso o
       * restore é GUARDADO (startEntryFocus), nunca setFocus cru.
       */
      restoreFocusKey: string
      /** Alvo SEMPRE montado da origem (o item de TopNav: `nav-home`, `nav-movies`, ...). */
      restoreSafeKey: string
    }
  | {
      kind: 'series-detail'
      id: string
      restoreFocusKey: string
      restoreSafeKey: string
    }
  | {
      kind: 'vod-player'
      sourceId: string
      resumeAt?: number
    }
  | {
      kind: 'series-player'
      seriesId: string
      episodeId: string
      resumeAt?: number
    }

/** As camadas de FICHA (carregam as keys de restauração de foco da origem). */
export type DetailLayer = Extract<OverlayLayer, { kind: 'movie-detail' | 'series-detail' }>
/** As camadas de PLAYER (o topo da pilha quando se está assistindo). */
export type PlayerLayer = Extract<OverlayLayer, { kind: 'vod-player' | 'series-player' }>

export function isDetailLayer(layer: OverlayLayer): layer is DetailLayer {
  return layer.kind === 'movie-detail' || layer.kind === 'series-detail'
}

export function isPlayerLayer(layer: OverlayLayer): layer is PlayerLayer {
  return layer.kind === 'vod-player' || layer.kind === 'series-player'
}

/**
 * Patch de um player já no topo (o `replacePlayer`). Achatado de propósito — o
 * `Partial<Omit<PlayerLayer,'kind'>>` distribui a união e o excess-property check
 * do TS rejeita `{ episodeId }` contra o membro vod. O caso real é o auto-advance
 * de série trocando `episodeId`/`resumeAt`; o caller patcheia os campos certos do
 * player que está no topo.
 */
export type PlayerPatch = Partial<{
  sourceId: string
  seriesId: string
  episodeId: string
  resumeAt: number
}>

// Referência imutável: cada mutação cria um array novo; o snapshot devolve a
// MESMA referência enquanto não muda (contrato do useSyncExternalStore).
let stack: readonly OverlayLayer[] = []
const listeners = new Set<() => void>()

function emit(): void {
  for (const l of listeners) l()
}

function setStack(next: readonly OverlayLayer[]): void {
  stack = next
  emit()
}

/** Leitura SÍNCRONA da pilha — pro árbitro de Back, que roda dentro do keydown. */
export function getStack(): readonly OverlayLayer[] {
  return stack
}

/**
 * Abre a ficha por cima da origem (card→ficha). Empurra uma camada de detalhe.
 * As `restore*Key` capturam o foco da origem AGORA, pra devolver no fecho.
 */
export function openDetail(layer: DetailLayer): void {
  setStack([...stack, layer])
}

/**
 * Play de dentro da ficha: empurra o player POR CIMA da ficha (que fica montada
 * embaixo, com temporada/episódio/scroll preservados). Não navega.
 */
export function pushPlayer(layer: PlayerLayer): void {
  setStack([...stack, layer])
}

/**
 * Auto-advance de série: TROCA o player do topo sem empilhar. No-op se o topo não
 * for um player. A troca chega no React por `useSyncExternalStore` (SyncLane), ANTES
 * de qualquer setState feito num efeito: quem precisa separar "antes" e "depois" da
 * troca chaveia o estado pela INSTÂNCIA da camada (cada chamada cria um objeto
 * novo). O guard v7 da tela de série, que dependia da lane do `navigate()`, NÃO
 * sobreviveu a esta troca (a cascata de 16/08; ver SeriesPlayerScreen).
 */
export function replacePlayer(patch: PlayerPatch): void {
  if (stack.length === 0) return
  const top = stack[stack.length - 1]
  if (!top || !isPlayerLayer(top)) return
  const nextTop = { ...top, ...patch } as OverlayLayer
  setStack([...stack.slice(0, -1), nextTop])
}

/**
 * Troca CONDICIONAL do player do topo: só troca se o topo ainda for EXATAMENTE a
 * camada `expected` — a MESMA instância (cada push/replace cria um objeto novo, então
 * a referência é o nonce da instância). Devolve se trocou.
 *
 * Existe pra quem decide DEPOIS de esperar alguma coisa (o failover entre cópias
 * espera a info da outra cópia): se nesse meio o usuário navegou, voltou, abriu outro
 * episódio ou outro filme, o topo já é outra instância e a troca NÃO acontece. O
 * `replacePlayer` incondicional trocaria QUALQUER player do topo, inclusive o de filme.
 */
export function replacePlayerIf(expected: OverlayLayer, patch: PlayerPatch): boolean {
  const top = stack[stack.length - 1]
  if (!top || top !== expected || !isPlayerLayer(top)) return false
  replacePlayer(patch)
  return true
}

/**
 * Fecha a camada do topo. Devolve a camada REMOVIDA (ou null se a pilha já
 * estava vazia) — o árbitro usa isso pra decidir o restore de foco: detalhe
 * removido → origem revelada → foco volta pro card; player removido → ficha
 * revelada → a ficha refoca o próprio Play (P1).
 */
export function pop(): OverlayLayer | null {
  if (stack.length === 0) return null
  const removed = stack[stack.length - 1] ?? null
  setStack(stack.slice(0, -1))
  return removed
}

/** Esvazia a pilha (deep-link com replace, hard-nav, logout). */
export function reset(): void {
  if (stack.length === 0) return
  setStack([])
}

/**
 * Assinatura FORA do React: o listener roda SÍNCRONO dentro da mutação (dentro do
 * `pushPlayer`/`pop`, ou seja, dentro do keydown que os disparou), antes de
 * qualquer render. É o que o `LivePlayerProvider` usa pra soltar o live quando a
 * pilha o cobre (e o decodificador, com um player por cima), no guard do live
 * (`overlayCoversLive`, #669), já em develop; e o que o `VodPrefetcher` usa pra
 * soltar o filme do prefetch antes de o player do filme abrir (AP-142):
 * reagir num efeito de render o amarraria à ordem de lane — o
 * `useSyncExternalStore` renderiza em SyncLane, ANTES de um `setState` comum, e um
 * guard que esperava os dois no mesmo render já produziu a cascata do auto-avanço.
 *
 * O listener deve ler a pilha por `getStack()` (nunca guardar cópia) e não pode
 * lançar: `emit` não isola listeners, e um throw aqui deixaria os seguintes (o do
 * React inclusive) sem aviso.
 */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Hook reativo: a pilha atual. O Host renderiza a partir daqui. */
export function useStack(): readonly OverlayLayer[] {
  return useSyncExternalStore(subscribe, getStack, getStack)
}
